<script setup>
import dayjs from 'dayjs'
import { ref, computed, onMounted } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useChatStore } from '@/stores/chat.store'
import { searchResultTypeEnum, useWebSiteStore } from '@/stores/web-site.store'

import { useNavigator } from '@/composables/navigator'
import { useCurrencyColumnFormatter } from '@/composables/data-grid'
import { useHighlightNewRow } from '@/composables/scroll'
import { useChips } from '@/composables/chips'
import { cloneCostReportRow, useLockedCostReportRow } from '@/composables/cost-report'
import { useExpenseClaimActions } from '@/composables/expense-claim'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

import CostReportRowEdit from '@/components/accounting/CostReportRowEdit.vue'
import CostReportRowListItem from '@/components/accounting/CostReportRowListItem.vue'
import ExpenseClaimDialog from '@/components/accounting/ExpenseClaimDialog.vue'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  hideProjectInfo: {
    type: Boolean,
    default: false,
  },
})

useChatStore().trackPageEntity(searchResultTypeEnum.PROJECT, () => props.project?.id)

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()

useNavigator(() => {
  if (!editItem.value) emit('cancel')
})

const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { canCreate } = useExpenseClaimActions()

const { costReportRowTypeEnum } = accountingStore
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { getProjectTagChips } = useChips()

const editItem = ref(null)
const rows = ref([])
const rowsContainer = ref(null)
const projectData = ref({ ...props.project })

const updateDatasource = async () => {
  const [rowsRes, projectRes] = await Promise.all([
    accountingStore.api.getCostReportRowsAsync({ project: props.project.id }),
    planningStore.fetchProjectByIdAsync(props.project.id),
  ])
  rows.value = rowsRes.data.sort((a, b) => (a.date || '').localeCompare(b.date || ''))
  projectData.value = projectRes.data
}

onMounted(async () => {
  await planningStore.fetchStaffsAsync({}, true)
  await accountingStore.fetchCostReportRowTagsAsync({}, true)
  await updateDatasource()
})

const groups = computed(() => [
  { id: 'time', title: i18n.t('common.costReportTime'), icon: 'mdi-clock-outline', type: costReportRowTypeEnum.TIME, rows: rows.value.filter(r => r.type === costReportRowTypeEnum.TIME) },
  { id: 'expense', title: i18n.t('common.expense'), icon: 'mdi-currency-eur', type: costReportRowTypeEnum.EXPENSE, rows: rows.value.filter(r => r.type === costReportRowTypeEnum.EXPENSE) },
])
const activeGroup = ref('time')
const newClaim = ref(null)

const openNewClaim = () => {
  if (canCreate()) newClaim.value = {}
}

const onClaimSaved = async() => {
  newClaim.value = null
  await updateDatasource()
}

const { isLocked } = useLockedCostReportRow()

const deleteConfirmRow = ref(null)

const showDeleteDialog = computed({
  get: () => deleteConfirmRow.value !== null,
  set: (val) => { if (!val) deleteConfirmRow.value = null },
})

const onDeleteRow = (row) => { deleteConfirmRow.value = row }

const onEditRow = (row) => { editItem.value = { ...row } }

const onCloneRow = (row) => {
  editItem.value = { ...cloneCostReportRow(row), project: props.project.id }
}

const onRowEditUpdate = async () => {
  const wasCreated = !editItem.value?.id
  const previousIds = new Set(rows.value.map(r => r.id))
  editItem.value = null
  await updateDatasource()
  emit('update')
  if (wasCreated) {
    const newRow = rows.value.find(r => !previousIds.has(r.id))
    await highlightRowAsync(newRow?.id, rowsContainer)
  }
}

const onConfirmDelete = async () => {
  await accountingStore.deleteCostReportRowAsync(deleteConfirmRow.value.id)
  deleteConfirmRow.value = null
  await updateDatasource()
  emit('update')
}

</script>

<template>
  <confirm-dialog
    v-model="showDeleteDialog"
    :message="i18n.t('common.confirmDelete')"
    icon="mdi-delete-outline"
    icon-color="negative"
    @confirm="onConfirmDelete"
  />

  <expense-claim-dialog
    :claim="newClaim"
    :project-id="props.project.id"
    @saved="onClaimSaved"
    @cancel="newClaim = null"
  />

  <q-dialog
    :model-value="editItem !== null"
    maximized
    persistent
  >
    <cost-report-row-edit
      v-if="editItem !== null"
      :model-value="editItem"
      :project="props.project"
      @update="onRowEditUpdate"
      @cancel="editItem = null"
    />
  </q-dialog>

  <q-card class="dialog-card-flex">
    <navigate-back
      v-if="!hideHeader"
      :text="project.name"
      @cancel="emit('cancel')"
    />

    <q-card-section class="q-pt-none q-px-sm">
      <q-list>
        <q-item v-if="!hideProjectInfo">
          <div style="width: 50%; float: left;">
            <q-item-section>
              <q-item-label caption>{{ i18n.t('common.period') }}</q-item-label>
              <q-item-label class="align-items-baseline">
                <q-icon name="mdi-clock-outline" class="q-mr-xs" />
                {{ dayjs(projectData.start).format('HH:mm') }} - {{ dayjs(projectData.end).format('HH:mm') }}
              </q-item-label>
              <q-item-label class="align-items-baseline">
                <q-icon name="mdi-calendar" class="q-mr-xs" />
                {{ dayjs(projectData.start).format('DD/MM') }} - {{ dayjs(projectData.end).format('DD/MM') }}
              </q-item-label>
            </q-item-section>
          </div>
          <div style="width: 50%; float: right;">
            <q-item-section style="display: flex; align-content: flex-end;">
              <q-item-label caption style="text-align: right;">{{ i18n.t('common.status') }}</q-item-label>
              <q-item-label>
                <chip-template
                  :values="[planningStore.getLocalizedProjectStatuses().find(i => i.id === projectData.status)]"
                  :style="{ 'margin-right': '0' }"
                />
              </q-item-label>
            </q-item-section>
          </div>
        </q-item>
        <q-item>
          <div v-if="!hideProjectInfo" style="width: 50%; float: left;">
            <q-item-section v-if="projectData.tag_ids?.length">
              <q-item-label caption>{{ i18n.t('common.tag', 2) }}</q-item-label>
              <q-item-label>
                <chip-template
                  :values="getProjectTagChips(projectData.tag_ids)"
                />
              </q-item-label>
            </q-item-section>
          </div>
          <div :style="hideProjectInfo ? 'width: 100%;' : 'width: 50%; float: right;'">
            <q-item-section style="display: flex; align-content: flex-end; align-items: flex-end;">
              <q-item-label caption style="text-align: right;">{{ i18n.t('common.totalCost') }}</q-item-label>
              <q-item-label style="text-align: right; font-weight: bold;">
                {{ projectData.total_cost != null ? useCurrencyColumnFormatter(projectData, 'total_cost', projectData.currency_ref) : '-' }}
              </q-item-label>
            </q-item-section>
          </div>
        </q-item>
        <q-item v-if="!hideProjectInfo && projectData.description">
          <q-item-section>
            <q-item-label caption>{{ i18n.t('common.description') }}</q-item-label>
            <q-item-label>{{ projectData.description }}</q-item-label>
          </q-item-section>
        </q-item>
        <q-item>
          <div style="width: 50%; float: left;">
            <q-item-section>
              <q-item-label caption>{{ i18n.t('common.totalWorkedHours') }}</q-item-label>
              <q-item-label>
                <q-icon name="mdi-briefcase-outline" class="q-mr-xs" />
                {{ projectData.total_worked_minutes != null ? (projectData.total_worked_minutes / 60).toFixed(2) : '-' }} {{ i18n.t('common.hoursAlt') }}
              </q-item-label>
            </q-item-section>
          </div>
          <div style="width: 50%; float: right;">
            <q-item-section style=" font-weight: bold; display: flex; align-content: flex-end;">
              <q-item-label caption style="text-align: right;">{{ i18n.t('common.totalReportedHours') }}</q-item-label>
              <q-item-label style="text-align: right;">
                <q-icon name="mdi-timer-outline" class="q-mr-xs" />
                {{ projectData.total_reported_minutes != null ? (projectData.total_reported_minutes / 60).toFixed(2) : '-' }} {{ i18n.t('common.hoursAlt') }}
              </q-item-label>
            </q-item-section>
          </div>
        </q-item>
        <q-item v-if="projectData.total_travel_minutes || projectData.total_pause_minutes">
          <div style="width: 50%; float: left;">
            <q-item-section>
              <q-item-label caption>{{ i18n.t('common.totalTravelMinutes') }}</q-item-label>
              <q-item-label>
                <q-icon name="mdi-car-outline" class="q-mr-xs" />
                {{ projectData.total_travel_minutes ? +(projectData.total_travel_minutes / 60).toFixed(2) : '-' }}h
              </q-item-label>
            </q-item-section>
          </div>
          <div style="width: 50%; float: right;">
            <q-item-section style="display: flex; align-content: flex-end;">
              <q-item-label caption style="text-align: right;">{{ i18n.t('common.totalPauseMinutes') }}</q-item-label>
              <q-item-label style="text-align: right;">
                <q-icon name="mdi-pause-circle-outline" class="q-mr-xs" />
                {{ projectData.total_pause_minutes ? +(projectData.total_pause_minutes / 60).toFixed(2) : '-' }} {{ i18n.t('common.hoursAlt') }}
              </q-item-label>
            </q-item-section>
          </div>
        </q-item>
      </q-list>
    </q-card-section>

    <q-tabs
      v-model="activeGroup"
      align="justify"
      active-color="primary"
      indicator-color="primary"
      dense
      no-caps
    >
      <q-tab
        v-for="group in groups"
        :key="group.id"
        :name="group.id"
        :icon="group.icon"
        :label="group.title"
      />
    </q-tabs>
    <q-separator />

    <q-card-section ref="rowsContainer" class="scroll col q-pa-none dialog-card-content-scroll">
      <q-tab-panels v-model="activeGroup" animated>
        <q-tab-panel
          v-for="group in groups"
          :key="group.id"
          :name="group.id"
          class="q-pa-sm"
        >
          <div class="row justify-end items-center q-gutter-x-sm">
            <q-btn
              v-if="group.type === costReportRowTypeEnum.EXPENSE && webSiteStore.hasStaff"
              style="padding: 0;"
              size="md"
              unelevated
              ripple
              rounded
              icon="mdi-receipt-text-plus-outline"
              @click="openNewClaim"
            >
              <q-tooltip>{{ i18n.t('common.expenseClaimNew') }}</q-tooltip>
            </q-btn>
            <q-btn
              style="padding: 0;"
              size="md"
              unelevated
              ripple
              rounded
              icon="mdi-plus-circle-outline"
              @click="editItem = { project: props.project.id, type: group.type }"
            />
          </div>

          <cost-report-row-list-item
            v-for="row in group.rows"
            :key="row.id"
            v-ripple
            :row="row"
            :currency-ref="props.project.currency_ref"
            :class="{ 'row-highlight': row.id === highlightedRowId }"
            clickable
            @click="onEditRow(row)"
          >
            <template #actions>
              <q-item-section avatar class="align-middle align-center q-px-none" style="flex-direction: row; gap: .125rem;">
                <q-icon name="mdi-content-copy" color="primary" @click.stop="onCloneRow(row)" />
                <q-icon
                  v-if="isLocked(row)"
                  name="mdi-magnify"
                  color="primary"
                  @click.stop="onEditRow(row)"
                />
                <q-icon
                  v-else
                  name="mdi-minus-circle-outline"
                  color="negative"
                  @click.stop="onDeleteRow(row)"
                />
              </q-item-section>
            </template>
          </cost-report-row-list-item>

          <div v-if="group.rows.length === 0" class="text-caption text-grey q-mt-sm q-ml-sm">
            -
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </q-card-section>
  </q-card>
</template>
